import React, { useEffect, useState } from 'react';
import { NumberField } from '@scale/shared/types/params/textcollection';
import { NumberFieldAnnotation } from '@scale/shared/types/response/textcollection';
import CustomTextField from './CustomTextField';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';

interface NumberFieldSectionProps {
  field: NumberField;
  annotation: NumberFieldAnnotation;
  setAnnotation: (annotation: NumberFieldAnnotation) => void;
  disabled?: boolean;
}

export default function NumberFieldSection(props: NumberFieldSectionProps) {
  const onChange = function (index: number, value: string): void {
    const response = props.annotation.response.slice();
    response[index] = value;
    const annotation: NumberFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };

  const [blurred, setBlurred] = React.useState(false);
  const onBlur = () => {
    setBlurred(true);
  };
  const onWheel = (e: $FIXME) => {
    e.target.blur();
  };

  const checkPrecision = (value: string, maxPrecision: number): boolean => {
    let split;
    if (value.includes(',')) {
      split = value.split(',');
    } else if (value.includes('.')) {
      split = value.split('.');
    } else {
      split = [value];
    }

    if (maxPrecision === 0) {
      if (split.length === 2) {
        return split[1]?.split('')?.every((char: string) => char === '0') ?? false;
      }
      return split.length === 1;
    }
    return split.length === 1 || (split.length === 2 && split[1].length <= maxPrecision);
  };

  const llmHelper = useLLMHelper();
  const [aiAssistant, setAiAssistant] = useState<string>('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);
  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content) setAiAssistant(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  const textFields = props.annotation.response.map((response: string, index: number) => {
    const isBlank = !response.trim();
    let errorText;
    if (blurred && props.field.required && isBlank) {
      errorText = 'This field cannot be empty';
    } else if (
      blurred &&
      props.field.max_precision !== undefined &&
      !checkPrecision(response, props.field.max_precision)
    ) {
      errorText = 'No more than ' + props.field.max_precision + ' decimal places allowed';
    }
    if (!isBlank && props.field.min !== undefined && Number(response) < props.field.min) {
      errorText = 'Response should be greater than or equal to ' + props.field.min;
    }
    if (!isBlank && props.field.max !== undefined && Number(response) > props.field.max) {
      errorText = 'Response should be less than or equal to ' + props.field.max;
    }
    const requiredStr = props.field.required ? ' *' : '';
    const title = `${props.field.title}${requiredStr}`;
    return (
      <div key={index}>
        <CustomTextField
          disabled={props.disabled}
          setIndexValue={onChange}
          type={'number'}
          index={index}
          value={response}
          title={title}
          description={props.field.description}
          hint={props.field.hint || props.field.instruction}
          shouldAutoComplete={false}
          onBlur={onBlur}
          errorText={errorText}
          onWheel={onWheel}
          rubric={props.field.rubric}
          defaultText={props.field.default_value}
        />
      </div>
    );
  });
  return (
    <>
      {(!!aiAssistant.length || aiAssistantLoading) && (
        <div
          style={{
            marginBottom: 8,
            padding: 8,
            whiteSpace: 'pre-wrap',
            backgroundColor: 'rgba(144, 202, 249, .2)',
            borderRadius: 8,
          }}
        >
          {aiAssistantLoading && !aiAssistant.length
            ? 'AI assistant evaluation is loading...'
            : `The AI assistant suggests: ${aiAssistant}`}
        </div>
      )}
      {textFields}
    </>
  );
}
